import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { auctionService } from '../../services/auctionService';
import { Auction, Bid, BidStatus } from '../../types/auction';

interface AuctionState {
  auctions: Auction[];
  currentAuction: Auction | null;
  bids: Bid[];
  bidStatus: BidStatus | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    auctionType?: string;
    status?: string;
    modelId?: number;
  };
}

const initialState: AuctionState = {
  auctions: [],
  currentAuction: null,
  bids: [],
  bidStatus: null,
  isLoading: false,
  error: null,
  filters: {},
};

export const fetchAuctions = createAsyncThunk(
  'auction/fetchAuctions',
  async (filters: any, { rejectWithValue }) => {
    try {
      const response = await auctionService.getAuctions(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load Game-a-Rama events');
    }
  }
);

export const fetchAuctionById = createAsyncThunk(
  'auction/fetchAuctionById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await auctionService.getAuctionById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load this Game-a-Rama event');
    }
  }
);

export const placeBid = createAsyncThunk(
  'auction/placeBid',
  async ({ auctionId, amount }: { auctionId: number; amount: number }, { rejectWithValue }) => {
    try {
      const response = await auctionService.placeBid(auctionId, amount);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit entry');
    }
  }
);

const auctionSlice = createSlice({
  name: 'auction',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<any>) => {
      state.filters = action.payload;
    },
    updateBidStatus: (state, action: PayloadAction<BidStatus>) => {
      state.bidStatus = action.payload;
    },
    addBidToList: (state, action: PayloadAction<Bid>) => {
      state.bids.unshift(action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Auctions
      .addCase(fetchAuctions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAuctions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.auctions = action.payload ?? [];
      })
      .addCase(fetchAuctions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Auction by ID
      .addCase(fetchAuctionById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAuctionById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAuction = action.payload ?? null;
      })
      .addCase(fetchAuctionById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Place Bid
      .addCase(placeBid.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(placeBid.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.bids.unshift(action.payload);
        }
      })
      .addCase(placeBid.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, updateBidStatus, addBidToList, clearError } = auctionSlice.actions;
export default auctionSlice.reducer;
